import type { Spell } from 'eolib';
import {HOTBAR_SLOT_COUNT,hotbarLabel} from '@/hotbar-keys';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { FaArrowUp } from 'react-icons/fa';
import { playSfxById, SfxId } from '@/sfx';
import { Button } from '@/ui/components';
import { UI_ITEM_BG, UI_PANEL_BORDER, UI_STICKY_BG } from '@/ui/consts';
import { useClient, useLocale } from '@/ui/context';
import { SlotType } from '@/ui/enums';
import { useBackdropBlur, useItemDrag, useSpellIconUrls } from '@/ui/in-game';
import { DialogBase } from './dialog-base';
import { skillSummary } from '@/skill-help';
import {masteryView,subscribeMastery,requestMastery,type MasteryView} from '@/spell-mastery';
import {SpellMasteryPanel} from './spell-mastery-panel';
import {StaffSpellSelector,isEquipmentSkill} from './staff-spell-panel';
import {SummoningPanel} from './summoning-panel';

// ---------------------------------------------------------------------------
// Individual spell card
// ---------------------------------------------------------------------------

type SpellCardProps = {
  spell: Spell;
  onCustomize:()=>void;
  view:MasteryView;
};

function SpellCard({ spell, onCustomize,view }: SpellCardProps) {
  const client = useClient();
  const { locale } = useLocale();
  const { startDrag } = useItemDrag();

  const record = client.getEsfRecordById(spell.id);
  const iconId = record?.iconId ?? null;
  const name =
    record?.name ??
    locale.shared.itemFallbackName.replace('{id}', String(spell.id));

  const spellUrls = useSpellIconUrls(iconId);


  const cardRef = useRef<HTMLDivElement>(null);

  const handlePointerDown = useCallback(
    (e: PointerEvent) => {
      e.stopPropagation();
      if ((e.target as Element).closest('button, select, input')) return;
      // Touch swipes scroll the spell list; use the existing hotkey selector
      // to assign spells on mobile. Mouse drag-to-hotbar remains available.
      if (e.pointerType === 'touch') return;
      e.preventDefault();
      const el = cardRef.current;
      if (!el) return;
      playSfxById(SfxId.InventoryPickup);
      startDrag({
        element: el,
        info: {
          source: 'spell',
          itemId: spell.id,
          pointerId: e.pointerId,
          ghostX: e.clientX,
          ghostY: e.clientY,
          offsetX: 17,
          offsetY: 16,
          graphicId: 0,
          ghostWidth: 34,
          ghostHeight: 32,
          ghostImageUrl: spellUrls?.normal,
        },
        onResolve: (result) => {
          if (result.type === 'hotbar-slot') {
            client.hotbarController.setSlot(result.index, {
              type: SlotType.Skill,
              typeId: spell.id,
            });
            playSfxById(SfxId.InventoryPlace);
          }
        },
      });
    },
    [spell.id, spellUrls, startDrag, client],
  );


  const levelLabel = locale.spells.level.replace(
    '{level}',
    String(spell.level),
  );

  return (
    <div
      ref={cardRef}
      data-spell-card={spell.id}
      class={`flex cursor-grab flex-col items-center gap-1 rounded border ${UI_PANEL_BORDER} ${UI_ITEM_BG} p-2 active:cursor-grabbing`}
      style={{ touchAction: 'pan-y' }}
      onPointerDown={handlePointerDown}
    >
      {/* Spell icon */}
      <div
        class={`flex h-12 w-12 items-center justify-center overflow-hidden rounded border ${UI_PANEL_BORDER} bg-base-300`}
      >
        {spellUrls ? (
          <img
            src={spellUrls.normal}
            alt=''
            class='h-8 w-8.5 object-contain'
            draggable={false}
          />
        ) : (
          <div class='h-8 w-8.5 rounded bg-base-content/10' />
        )}
      </div>

      {/* Name */}
      <button type='button' onClick={onCustomize} class='wrap-break-word line-clamp-2 w-full text-center font-semibold text-xs leading-tight'>
        {name}
      </button>

      {/* Level */}
      <p class='text-primary/60 text-xs'>{levelLabel}</p>
      {skillSummary(spell.id, name) && (
        <p class='w-full text-center text-xs leading-snug text-base-content/70'>
          {skillSummary(spell.id, name)}
        </p>
      )}
      <select
        aria-label={`Hotbar slot for ${name}`}
        class='select select-xs w-full'
        value=''
        onChange={(e) => {
          const index = Number(e.currentTarget.value);
          if (Number.isInteger(index) && index >= 0 && index < HOTBAR_SLOT_COUNT) {
            client.hotbarController.setSlot(index, {type: SlotType.Skill, typeId: spell.id});
            playSfxById(SfxId.InventoryPlace);
          }
          e.currentTarget.value = '';
        }}
      >
        <option value='' disabled>Assign hotbar...</option>
        {Array.from({length: HOTBAR_SLOT_COUNT}, (_, i) => <option value={i}>{hotbarLabel(i)}</option>)}
      </select>

      {/* Mastery keeps spell icons draggable to the hotbar. */}
      {isEquipmentSkill(client,spell.id,view)&&<Button variant={['xs','primary']} class='w-full'
       disabled={!view.staff?.key||view.pending}
       onClick={()=>requestMastery(client,'equip_staff',view.staff?.spell_id===spell.id?0:spell.id,view.staff!.key!)}>
       {view.staff?.spell_id===spell.id?'Remove from Weapon':'Equip to Weapon'}
      </Button>}
      <Button
        variant={['xs', 'primary']}
        class='w-full'
        onClick={onCustomize}
      >
        <FaArrowUp size={10} />
        Customize
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SpellsDialog
// ---------------------------------------------------------------------------

export function SpellsDialog() {
  const client = useClient();
  const { locale } = useLocale();
  const blur = useBackdropBlur();

  const [spells, setSpells] = useState<Spell[]>(() => [...client.spells]);
  const [view,setView]=useState(()=>masteryView(client));
  const [selected,setSelected]=useState<number|null>(null);
  useEffect(()=>{
    const off=subscribeMastery(client,()=>{setView({...masteryView(client)});setSpells([...client.spells]);});
    const refresh=()=>requestMastery(client);client.inventoryController.subscribeEquipmentChanged(refresh);
    requestMastery(client);
    return ()=>{off();client.inventoryController.unsubscribeEquipmentChanged(refresh);};
  },[client]);

  useEffect(() => {
    const onSkillsChanged = () => {
      setSpells([...client.spells]);
      requestMastery(client);
    };
    client.statSkillController.subscribeSkillsChanged(onSkillsChanged);
    return () => {
      client.statSkillController.unsubscribeSkillsChanged(onSkillsChanged);
    };
  }, [client]);


  return (
    <DialogBase id='spells' title={selected===null?locale.spells.title:'Spell Mastery'} size='lg' avoidBottom touchScroll>
      {selected!==null?<SpellMasteryPanel id={selected} view={view} onBack={()=>setSelected(null)}/>:<>
      <div class='p-2'><StaffSpellSelector view={view}/></div>
      <SummoningPanel/>
      {/* Skill points header */}
      <div
        class={`sticky top-0 z-10 ${UI_PANEL_BORDER} border-b ${UI_STICKY_BG} px-3 py-1.5 ${blur}`}
      >
        <p class='text-center font-medium text-primary text-sm'>
          Use spells to earn mastery · Customize each spell
        </p>
      </div>

      {spells.length === 0 ? (
        <p class='py-6 text-center text-base-content/50 text-sm'>
          {locale.spells.noSpells}
        </p>
      ) : (
        <div class='grid max-h-80 grid-cols-2 gap-2 overflow-y-auto p-2'>
          {spells.map((spell) => (
            <SpellCard key={spell.id} spell={spell} view={view} onCustomize={()=>{setSelected(spell.id);requestMastery(client);}} />
          ))}
        </div>
      )}
      </>}
    </DialogBase>
  );
}
